import React, { useState, useEffect, useCallback } from 'react';
import {
    Row,
    Col,
    Form,
    Card,
    Spinner,
    Alert,
    Pagination,
    Collapse,
    Button,
} from 'react-bootstrap';

import { useUserApi } from '@/shared/services/userApi';
import useChatPageEffect from '@/shared/hooks/useChatPageEffect';
import useEcosystemTaxonomy from '@/shared/hooks/useEcosystemTaxonomy';
import { AVAILABLE_FOR_OPTIONS } from '@/shared/constants/mentorConstants';

import MentorCard from './MentorCard';

const ITEMS_PER_PAGE = 12;

export default function MentorDirectory() {
    const { mentorAudiences } = useEcosystemTaxonomy();
    const [mentors, setMentors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        audience: '',
        availableFor: '',
        expertise: '',
    });

    const userApi = useUserApi();

    // Publish page context for chat widget
    useChatPageEffect(
        {
            page: 'mentors',
            searchQuery: debouncedQuery || null,
            activeFilters: {
                audience: filters.audience || null,
                availableFor: filters.availableFor || null,
                expertise: filters.expertise || null,
            },
            totalMentors: total,
        },
        [
            debouncedQuery,
            filters.audience,
            filters.availableFor,
            filters.expertise,
            total,
        ]
    );

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(searchQuery);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters]);

    const fetchMentors = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // If there's a keyword search, use the search endpoint
            if (debouncedQuery.trim().length >= 2) {
                const data = await userApi.searchMentors(debouncedQuery);
                setMentors(data.mentors || []);
                setTotal(data.count || 0);
                setTotalPages(1); // Search endpoint doesn't paginate
            } else {
                // Use the list endpoint with filters
                const params = {
                    page: currentPage,
                    limit: ITEMS_PER_PAGE,
                };
                if (filters.audience) params.audience = filters.audience;
                if (filters.availableFor) {
                    params.availableFor = filters.availableFor;
                }
                if (filters.expertise) params.expertise = filters.expertise;

                const data = await userApi.fetchMentors(params);
                setMentors(data.mentors || []);
                setTotal(data.total || 0);
                setTotalPages(data.totalPages || 1);
            }
        } catch (err) {
            console.error('Error fetching mentors:', err);
            setError('Failed to load mentors. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [debouncedQuery, currentPage, filters, userApi]);

    useEffect(() => {
        fetchMentors();
    }, [fetchMentors]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const clearFilters = () => {
        setFilters({ audience: '', availableFor: '', expertise: '' });
        setSearchQuery('');
    };

    const hasActiveFilters =
        filters.audience || filters.availableFor || filters.expertise;

    // Build pagination items
    const paginationItems = [];
    for (let i = 1; i <= totalPages; i++) {
        paginationItems.push(
            <Pagination.Item
                key={i}
                active={i === currentPage}
                onClick={() => setCurrentPage(i)}
            >
                {i}
            </Pagination.Item>
        );
    }

    return (
        <>
            {/* Search and Filters */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-4">
                    <Form onSubmit={(e) => e.preventDefault()}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-medium">
                                Search Mentors
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Search by name, expertise, industry, or bio..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </Form.Group>

                        <div className="d-flex justify-content-between align-items-center">
                            <Button
                                variant="link"
                                size="sm"
                                className="text-decoration-none p-0"
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                {showFilters ? 'Hide Filters' : 'Show Filters'}
                                {hasActiveFilters && (
                                    <span className="ms-1 badge bg-primary rounded-pill">
                                        !
                                    </span>
                                )}
                            </Button>
                            {hasActiveFilters && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="text-decoration-none p-0 text-muted"
                                    onClick={clearFilters}
                                >
                                    Clear All
                                </Button>
                            )}
                        </div>

                        <Collapse in={showFilters}>
                            <div>
                                <hr />
                                <Row className="g-3">
                                    {mentorAudiences.length > 0 && (
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-medium">
                                                    Preferred Audience
                                                </Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    value={filters.audience}
                                                    onChange={(e) =>
                                                        handleFilterChange(
                                                            'audience',
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    <option value="">
                                                        All Audiences
                                                    </option>
                                                    {mentorAudiences.map(
                                                        (audience) => (
                                                            <option
                                                                key={audience}
                                                                value={audience}
                                                            >
                                                                {audience}
                                                            </option>
                                                        )
                                                    )}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    )}
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="small fw-medium">
                                                Available For
                                            </Form.Label>
                                            <Form.Select
                                                size="sm"
                                                value={filters.availableFor}
                                                onChange={(e) =>
                                                    handleFilterChange(
                                                        'availableFor',
                                                        e.target.value
                                                    )
                                                }
                                            >
                                                <option value="">
                                                    All Types
                                                </option>
                                                {AVAILABLE_FOR_OPTIONS.map(
                                                    (opt) => (
                                                        <option
                                                            key={opt.value}
                                                            value={opt.value}
                                                        >
                                                            {opt.label}
                                                        </option>
                                                    )
                                                )}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="small fw-medium">
                                                Expertise Area
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                placeholder="e.g., Data Science"
                                                value={filters.expertise}
                                                onChange={(e) =>
                                                    handleFilterChange(
                                                        'expertise',
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                        </Collapse>
                    </Form>
                </Card.Body>
            </Card>

            {/* Error */}
            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {/* Results Summary */}
            {!loading && (
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <small className="text-muted">
                        {total} mentor{total !== 1 ? 's' : ''} found
                    </small>
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Loading mentors...</p>
                </div>
            )}

            {/* Results Grid */}
            {!loading && mentors.length > 0 && (
                <Row className="g-4 mb-4">
                    {mentors.map((mentor) => (
                        <Col md={6} lg={4} key={mentor._id}>
                            <MentorCard
                                mentor={mentor}
                                onExpertiseClick={(area) => {
                                    // Pivot from keyword search to a filtered
                                    // browse: clear the search box, apply the
                                    // expertise filter, and reveal the panel.
                                    setSearchQuery('');
                                    handleFilterChange('expertise', area);
                                    setShowFilters(true);
                                }}
                            />
                        </Col>
                    ))}
                </Row>
            )}

            {/* Empty State */}
            {!loading && mentors.length === 0 && (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <h5 className="text-muted mb-3">No mentors found</h5>
                        <p className="text-muted mb-3">
                            {debouncedQuery || hasActiveFilters
                                ? 'Try adjusting your search or filters to find mentors.'
                                : 'No mentors have opted in yet. Check back soon!'}
                        </p>
                        {(debouncedQuery || hasActiveFilters) && (
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="rounded-pill"
                                onClick={clearFilters}
                            >
                                Clear Search & Filters
                            </Button>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
                <div className="d-flex justify-content-center">
                    <Pagination>
                        <Pagination.Prev
                            disabled={currentPage === 1}
                            onClick={() =>
                                setCurrentPage((p) => Math.max(1, p - 1))
                            }
                        />
                        {paginationItems}
                        <Pagination.Next
                            disabled={currentPage === totalPages}
                            onClick={() =>
                                setCurrentPage((p) =>
                                    Math.min(totalPages, p + 1)
                                )
                            }
                        />
                    </Pagination>
                </div>
            )}
        </>
    );
}
